import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import EvidenceBlock from '../GscTools/components/EvidenceBlock';
import ScopeControls from '../GscTools/components/ScopeControls';
import { downloadCsv } from '../GscTools/csv';
import { ELIGIBLE_PERMISSIONS } from '../GscTools/query';
import { readParams, writeParams } from '../GscTools/url';
import Loading from '../../../components/Loading';

import RecordFilter from './additionalBox';
import IndexChildren from './components/IndexChildren';
import { buildRecordsCsv } from './exportRows';
import { CONTENT_COLUMNS, DETAIL_COLUMNS, ISSUE_COLUMNS, RECORD_COLUMNS } from './keys';
import { listSitemaps } from './query';
import {
    FILTERS,
    contentRows,
    detailRows,
    filterRecords,
    formatTime,
    issueRows,
    recordRows,
    sitemapName,
} from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';

const IDLE_CHILDREN = { status: 'idle', path: null, rows: [], error: null };

/**
 * Sitemap Status — the sitemaps submitted for one property, as Search Console records them.
 *
 * A submission record, not an index census. Each record says what was submitted and what Google
 * did with the submission: when it was submitted and downloaded, whether processing is pending,
 * whether it is an index, how many warnings and errors Google counted, and how many entries of each
 * content type it holds. None of that is a count of indexed pages or an inventory of the site's
 * URLs, and the page says so wherever a number could be taken for either. A count Google did not
 * return is "Not returned", never a zero.
 *
 * Read-only and on request. Nothing here submits or deletes a sitemap, fetches a sitemap file or
 * crawls a URL, and nothing refreshes on its own: one list request when the scope is applied,
 * another when someone presses Refresh, and — for a sitemap index, when asked — one list request
 * narrowed to that index. The record filter works over the records already returned.
 *
 * A failed refresh never empties the page. The last records for the same property stay on screen,
 * labelled with when they were fetched, and the failure is reported beside them — an error is not
 * an empty list, and an empty list is not proof the site has no sitemap. Nothing is stored; the
 * property lives in the page's address, so a reload or a shared link reads the same property again.
 */
class SitemapStatus extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleFilter = this.handleFilter.bind(this);
        this.handleSelectRecord = this.handleSelectRecord.bind(this);
        this.handleLoadChildren = this.handleLoadChildren.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);
        this.recordFilter = this.recordFilter.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();

        // Bumped by every list request; a response carrying an older value is stale.
        this.generation = 0;
        this.childrenGeneration = 0;
        this.isMounted_ = false;

        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,

            draft: { ...initialDraft(), ...this.urlDraft },

            applied: null,
            loading: false,
            // Held apart from the result: a failed refresh reports itself without discarding the
            // last records returned for the same property. `quota` marks a spent API quota.
            error: null,
            // The most recent successful list, exactly as the endpoint returned it.
            result: null,
            controlsOpen: true,
            capturing: false,

            filter: 'all',
            selectedPath: null,
            children: { ...IDLE_CHILDREN },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's records: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a list being read under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!requested;

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState((state) => {
            const draft = { ...state.draft, ...patch };

            if (patch.siteUrl) {
                const property = state.properties.find((item) => item.siteUrl === patch.siteUrl);
                draft.permissionLevel = property?.permissionLevel ?? '';
            }

            return { draft };
        });
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length || !this.credentialsDomainId) return;
        this.load(scope, { keepControls: false });
    }

    /**
     * Refresh repeats the applied scope. It never reopens the controls and never clears the
     * records first: until the new response lands, what is on screen is the last successful list.
     */
    handleRefresh() {
        if (this.state.applied && !this.state.loading) {
            this.load(this.state.applied, { keepControls: true });
        }
    }

    async load(scope, { keepControls }) {
        this.generation += 1;
        this.childrenGeneration += 1;
        const generation = this.generation;

        writeParams(reportParamsFromScope(scope), URL_KEYS);

        // Another property's records are cleared at once; the same property's stay until the new
        // list replaces them, so a failed refresh still has something true to show.
        const sameProperty = this.state.result?.site_url === scope.siteUrl;
        this.setState((state) => ({
            applied: scope,
            controlsOpen: keepControls ? state.controlsOpen : false,
            loading: true,
            error: null,
            result: sameProperty ? state.result : null,
            selectedPath: sameProperty ? state.selectedPath : null,
            children: { ...IDLE_CHILDREN },
        }));

        try {
            const result = await listSitemaps({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: scope.siteUrl,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const paths = (result.sitemaps ?? []).map((sitemap) => sitemap.path);

            // Replaced in one step. The picked record survives a refresh while it is still
            // returned; otherwise the first record is shown, so blocks 02 and 03 are never empty
            // while there is something to put in them.
            this.setState((state) => ({
                loading: false,
                error: null,
                result,
                selectedPath: paths.includes(state.selectedPath)
                    ? state.selectedPath
                    : paths[0] ?? null,
            }));
        } catch (err) {
            console.log('sitemaps', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            this.setState({
                loading: false,
                error: {
                    message: err?.message ?? this.props.t('global.something_went_wrong'),
                    quota: !!err?.quota,
                },
            });
        }
    }

    handleFilter(filter) {
        this.setState({ filter });
    }

    /** Picking a record re-points blocks 02 and 03; a different record's children are dropped. */
    handleSelectRecord(item) {
        if (item.path === this.state.selectedPath) return;
        this.childrenGeneration += 1;
        this.setState({ selectedPath: item.path, children: { ...IDLE_CHILDREN } });
    }

    /** Lists the sitemaps submitted inside the selected index — only when asked. */
    async handleLoadChildren() {
        const { applied, selectedPath } = this.state;
        if (!applied || !selectedPath) return;

        this.childrenGeneration += 1;
        const generation = this.childrenGeneration;

        this.setState({
            children: { ...IDLE_CHILDREN, status: 'loading', path: selectedPath },
        });

        try {
            const result = await listSitemaps({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl: applied.siteUrl,
                sitemapIndex: selectedPath,
            });

            if (!this.isMounted_ || generation !== this.childrenGeneration) return;

            this.setState({
                children: {
                    status: 'done',
                    path: selectedPath,
                    rows: recordRows(this.props.t, result.sitemaps),
                    error: null,
                },
            });
        } catch (err) {
            console.log('sitemap index', err);
            if (!this.isMounted_ || generation !== this.childrenGeneration) return;

            this.setState({
                children: {
                    ...IDLE_CHILDREN,
                    status: 'error',
                    path: selectedPath,
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    exportName() {
        const result = this.state.result;
        return `sitemap-status-${result?.site_url ?? 'property'}-${
            result?.fetched_at ? moment(result.fetched_at).format('YYYY-MM-DD-HHmm') : ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    filterLabel() {
        return this.props.t(`tools.sitemap_status.filters.${this.state.filter}`);
    }

    /** Block 04's export: the records in the current filter, with the scope they were read under. */
    handleExport() {
        const { applied, result, filter } = this.state;
        if (!applied || !result) return;

        const records = recordRows(this.props.t, result.sitemaps);

        downloadCsv(
            buildRecordsCsv({
                t: this.props.t,
                scope: applied,
                filterLabel: this.filterLabel(),
                fetchedAt: result.fetched_at,
                rows: filterRecords(records, filter),
                returned: records.length,
            }),
            `${this.exportName()}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the records on screen as an image — no further request and no upload. The controls
     * and export actions are left out of the frame; the property and the fetch time stay in it.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    element.classList?.contains('gsc-tool-icon-actions') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName()}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /** What the collapsed scope bar states, since this report has no window to put there. */
    summary() {
        const { t } = this.props;
        const result = this.state.result;

        return result ? (
            <>
                {result.site_url}
                <small>
                    {t('tools.sitemap_status.fetched', { time: formatTime(result.fetched_at) })}
                </small>
            </>
        ) : (
            <>{t('tools.sitemap_status.summary_idle')}</>
        );
    }

    recordFilter(records) {
        const counts = Object.fromEntries(
            FILTERS.map((name) => [name, filterRecords(records, name).length]),
        );

        return (
            <RecordFilter filter={this.state.filter} counts={counts} onFilter={this.handleFilter} />
        );
    }

    renderState(icon, title, body, error) {
        return (
            <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                <div className="card-body gsc-tool-load-state">
                    <span className="gsc-tool-load-icon" aria-hidden="true">
                        <i className={icon} />
                    </span>
                    <h4>{title}</h4>
                    {body}
                    {!!error && <div className="gsc-tool-banner error">{error}</div>}
                </div>
            </div>
        );
    }

    renderReport() {
        const { t } = this.props;
        const { result, error, loading, filter, selectedPath, children } = this.state;
        const fetchedAt = formatTime(result.fetched_at);
        const fileName = (title) => `${title} — ${this.exportName()}`;

        const records = recordRows(t, result.sitemaps);
        const shown = filterRecords(records, filter);
        const selected = records.find((row) => row.path === selectedPath) ?? null;
        const selectedName = selected ? sitemapName(selected.path) : null;

        return (
            <>
                {loading && (
                    <div className="gsc-tool-banner">
                        {t('tools.sitemap_status.refreshing', { time: fetchedAt })}
                    </div>
                )}

                {/* A failed refresh is never presented as a new list: the records below are the
                    previous ones, and the banner says when they are from. */}
                {!!error && (
                    <div className="gsc-tool-banner error">
                        {error.quota
                            ? t('tools.sitemap_status.stale_quota', { time: fetchedAt })
                            : t('tools.sitemap_status.stale_error', {
                                  time: fetchedAt,
                                  reason: error.message,
                              })}
                    </div>
                )}

                <EvidenceBlock
                    number="01"
                    title={t('tools.sitemap_status.records.title')}
                    description={t('tools.sitemap_status.records.description', {
                        property: result.site_url,
                        time: fetchedAt,
                    })}
                    columns={RECORD_COLUMNS(t)}
                    rows={shown}
                    fileName={fileName(t('tools.sitemap_status.records.title'))}
                    emptyMessage={
                        records.length
                            ? t('tools.sitemap_status.records.filtered_empty')
                            : t('tools.sitemap_status.records.empty')
                    }
                    tableProps={{
                        additionalBox: () => this.recordFilter(records),
                        rowClassName: (item) => (item.path === selectedPath ? 'dt-row-picked' : ''),
                        onRowClick: this.handleSelectRecord,
                    }}
                    notes={[
                        t('tools.sitemap_status.records.select_hint'),
                        t('tools.sitemap_status.records.not_indexed'),
                        t('tools.sitemap_status.records.no_fetch'),
                    ]}
                />

                <EvidenceBlock
                    number="02"
                    title={t('tools.sitemap_status.details.title')}
                    description={
                        selected
                            ? t('tools.sitemap_status.details.description', {
                                  name: selectedName,
                              })
                            : t('tools.sitemap_status.details.none')
                    }
                    columns={DETAIL_COLUMNS(t)}
                    rows={selected ? detailRows(t, selected.sitemap) : []}
                    fileName={fileName(
                        `${t('tools.sitemap_status.details.title')} ${selectedName}`,
                    )}
                    emptyMessage={t('tools.sitemap_status.details.none')}
                    notes={[
                        t('tools.sitemap_status.details.flags_note'),
                        t('tools.sitemap_status.details.pending_note'),
                    ]}
                >
                    <IndexChildren
                        record={selected}
                        state={children}
                        onLoad={this.handleLoadChildren}
                    />
                </EvidenceBlock>

                <EvidenceBlock
                    number="03"
                    title={t('tools.sitemap_status.contents.title')}
                    description={
                        selected
                            ? t('tools.sitemap_status.contents.description', {
                                  name: selectedName,
                              })
                            : t('tools.sitemap_status.details.none')
                    }
                    columns={CONTENT_COLUMNS(t)}
                    rows={selected ? contentRows(t, selected.sitemap) : []}
                    fileName={fileName(
                        `${t('tools.sitemap_status.contents.title')} ${selectedName}`,
                    )}
                    emptyMessage={
                        selected
                            ? t('tools.sitemap_status.contents.not_returned')
                            : t('tools.sitemap_status.details.none')
                    }
                    notes={[
                        t('tools.sitemap_status.contents.not_indexed'),
                        t('tools.sitemap_status.contents.no_sum'),
                    ]}
                />

                <EvidenceBlock
                    number="04"
                    title={t('tools.sitemap_status.issues.title')}
                    description={t('tools.sitemap_status.issues.description', {
                        count: records.length,
                    })}
                    columns={ISSUE_COLUMNS(t)}
                    rows={issueRows(t, records, {
                        fetchedAt: result.fetched_at,
                        filterLabel: this.filterLabel(),
                        shown: shown.length,
                    })}
                    fileName={fileName(t('tools.sitemap_status.issues.title'))}
                    actions={
                        <button
                            type="button"
                            title={t('tools.sitemap_status.export.button')}
                            aria-label={t('tools.sitemap_status.export.button')}
                            onClick={this.handleExport}
                        >
                            <i className="icon-fi_download" />
                        </button>
                    }
                    notes={[
                        t('tools.sitemap_status.issues.counts_note'),
                        t('tools.sitemap_status.issues.export_note'),
                        t('tools.sitemap_status.issues.read_only'),
                    ]}
                />

                <p className="gsc-tool-table-note">{t('tools.sitemap_status.footnote')}</p>
            </>
        );
    }

    renderBody() {
        const { t } = this.props;
        const { applied, result, loading, error } = this.state;

        if (!applied) {
            return this.renderState(
                'icon-pages',
                t('tools.sitemap_status.idle_title'),
                <p>{t('tools.sitemap_status.idle')}</p>,
            );
        }

        if (!result && loading) {
            return this.renderState(
                'icon-pages',
                t('tools.sitemap_status.loading_title'),
                <Loading padding={10} />,
            );
        }

        if (!result) {
            const reason = error?.quota ? 'quota' : 'failed';
            return this.renderState(
                'icon-pages',
                t('tools.sitemap_status.failed_title'),
                <p>{t(`tools.sitemap_status.${reason}`)}</p>,
                error?.message,
            );
        }

        return this.renderReport();
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = !!this.state.result;

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.sitemap_status.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.sitemap_status.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    // No window, no surface, no comparison and no daily data state: the property is
                    // the whole scope, and the collapsed bar states it with its fetch time where the
                    // date range would otherwise be. The record filter belongs to block 01's table.
                    fields={['property']}
                    hidePreliminary
                    summary={this.summary()}
                    applyLabel={t('tools.sitemap_status.controls.load')}
                    applyDisabled={!this.credentialsDomainId}
                    extraScopeLabels={applied ? [applied.siteUrl] : []}
                />

                {this.renderBody()}
            </div>
        );
    }
}

export default withTranslation()(SitemapStatus);
